/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useState } from "react";
import { Button, message, Modal, Upload, Card, Select } from "antd";
import Draggable from "react-draggable";
import ResizableAntdTable from "resizable-antd-table";
import { CloseCircleOutlined, ImportOutlined } from "@ant-design/icons";
import type { UploadProps } from "antd/es/upload/interface";
import { useCustomEventListener } from "react-custom-events";

import { ValidationTableContainer } from "./validation-table.styles";
import ValidationTableColumns from "./ValidationTableColumns";
import { ControlPointItem, ControlPointsImportData, ProjectRow } from "../../../types/interfaces";
import { ControlPointsImportModal } from "../../modal/ControlPointsImportModal/ControlPointsImportModal";
import { deleteControlPointItemData, importFileData, updateControlPointItemData } from "./utilsValidation";
import { addTie, flyToProject, removeMeasure, selectTie, showControlPoint } from "./utilsValidationGeo";
import { addTiePointStore, DataStore, removeTiePointStore } from "../../../store/DataStore";

const cardStyle1: React.CSSProperties = {
    position: "absolute",
    width: 450,
    top: 330,
    right: 10,
    color: "white",
    background: "#303336cc"
};

interface ValidationTableProps {
    show: boolean;
    setShowValidationTable: (value: boolean) => void;
}

export const ValidationTable = ({ show, setShowValidationTable }: ValidationTableProps) => {
    const [dataLoading] = useState<boolean>(false);
    const [tableData, setTableData] = useState<ControlPointItem[]>([]);
    const [selectedPointItemId, setSelectedPointItemId] = useState("");
    const [tiePointItemId, setTiePointItemId] = useState("");
    const [haveTieFromSelectedItem, setHaveTieFromSelectedItem] = useState(false);
    const [fileContent, setFileContent] = useState<string>("");
    const [showImportModal, setShowImportModal] = useState(false);
    const [projectList, setProjectList] = useState<ProjectRow[]>([]);
    const [selectedProject, setSelectedProject] = useState<ProjectRow | undefined>(undefined);
    const [selectedEpsg, setSelectedEpsg] = useState<string>("");
    const [fields, setFields] = useState<string[]>([]);

    const [showConfirmTieModal, setConfirmTieModal] = useState(false);

    const onDelete = (index: string) => {
        if (selectedProject) {
            const result = deleteControlPointItemData(selectedProject.id, index);
            if (result) {
                removeMeasure(index);
                setTableData(result);

                removeTiePointStore(selectedProject.project, index);
            }
        }
    };

    const onSelectTie = (index: string) => {
        const pointItem = tableData.find((p) => p.no === index);
        if (pointItem) {
            selectTie(
                pointItem,
                tableData.find((p) => p.no === selectedPointItemId)
            );
            setSelectedPointItemId(pointItem.no);
        }
    };

    const onShow = (index: string, value: boolean) => {
        if (selectedProject) {
            const result = updateControlPointItemData(selectedProject.id, index, {
                show: value
            });
            if (result) {
                setTableData(result);
            }

            showControlPoint(selectedProject.id, index);
        }
    };

    const onChangeTitle = (index: string, e: any) => {
        if (selectedProject) {
            const result = updateControlPointItemData(selectedProject.id, index, {
                title: e.target.value
            });
            if (result) {
                setTableData(result);
            }
        }
    };

    const columns = ValidationTableColumns(onDelete, onShow, onSelectTie, onChangeTitle);

    const loadControlPoints = () => {
        if (!window.cognitoUser) {
            console.warn("There is no any cognitoUser");
        }
    };

    const importData = (values: ControlPointsImportData, projects: ProjectRow[]) => {
        const data = importFileData(fileContent, fields, values);
        if (!data) {
            message.warning("Any issue maybe caused because of EPSG you selected. Please check logs on console.");
            return;
        }

        const allProjectData = DataStore.allProjectData;

        let item = allProjectData.find((p) => p.id === values.projectId);
        if (item) {
            if (item.data) {
                item.data = [...item.data, ...data];
            } else {
                item.data = data;
            }
        } else {
            const project = projects.find((p) => p.id === values.projectId);
            if (project) {
                item = {
                    id: project.id,
                    project: project.project,
                    epsg: values.epsg,
                    data: data
                };
                DataStore.allProjectData = [...allProjectData, item];
            }
        }
        setTableData(item ? [...item.data] : []);
        setSelectedEpsg(values.epsg);
        setShowImportModal(false);

        if (item) {
            flyToProject(item.data);
        }
    };

    useCustomEventListener("ClickTiePoint", (data: { id: string; haveTie: boolean }) => {
        setTiePointItemId(data.id);
        setConfirmTieModal(true);
        setHaveTieFromSelectedItem(data.haveTie);
    });

    const hideConfirmTieModal = () => {
        setConfirmTieModal(false);
    };

    const handlerConfirm = () => {
        const pointItem = tableData.find((p) => p.no === selectedPointItemId);
        const tiePointItem = tableData.find((p) => p.no === tiePointItemId);
        if (selectedProject && pointItem && tiePointItem) {
            addTie(pointItem, tiePointItem, haveTieFromSelectedItem);

            addTiePointStore(selectedProject.project, pointItem.no, tiePointItem);
        }
        setTiePointItemId("");
        setConfirmTieModal(false);
        setHaveTieFromSelectedItem(false);
    };

    useEffect(() => {
        loadControlPoints();
    }, []);

    const props: UploadProps = {
        beforeUpload: (file) => {
            if (file.type === "text/csv") {
                const read = new FileReader();
                read.readAsText(file);
                read.onloadend = () => {
                    if (typeof read.result === "string") {
                        setFileContent(read.result);

                        const lines = read.result.split("\n");
                        if (lines.length > 0) setFields(lines[0].trim().split(","));

                        setShowImportModal(true);
                    }
                };
            } else {
                message.warning("Please select correct file with csv extension.");
            }

            return false;
        }
    };

    return show ? (
        <ValidationTableContainer>
            <Draggable>
                <Card
                    id="validationTable"
                    title="Validation"
                    extra={
                        <div className="extra-div">
                            <Upload {...props}>
                                <Button icon={<ImportOutlined />} />
                            </Upload>
                            <Button icon={<CloseCircleOutlined />}
                            onClick={() => {
                                setShowValidationTable(false);
                            }}
                             />
                        </div>
                    }
                    style={cardStyle1}
                >
                    <div className="project-data">
                        <Select
                            value={selectedProject?.id}
                            options={projectList.map((item) => ({
                                label: item.project,
                                value: item.id
                            }))}
                            style={{ width: 200, marginBottom: 10 }}
                            onChange={(value) => {
                                const projectItem = projectList.find((p) => p.id === value);
                                setSelectedProject(projectItem);

                                const tbData = DataStore.allProjectData.find((p) => p.id === value);
                                if (tbData) {
                                    setTableData(tbData.data);
                                }
                                if (projectItem) {
                                    setSelectedEpsg(projectItem.epsg || "");
                                }
                            }}
                        />
                        <span className="epsg">{selectedEpsg ? `EPSG: ${selectedEpsg}` : ""}</span>
                    </div>

                    <ResizableAntdTable
                        stick
                        style={{ fontSize: 11 }}
                        loading={dataLoading}
                        columns={columns}
                        bordered
                        rowKey="no"
                        size="small"
                        dataSource={tableData}
                        pagination={false}
                        scroll={{ x: 400, y: 400 }}
                    />
                </Card>
            </Draggable>
            <Modal
                title="Confirm"
                open={showConfirmTieModal}
                onOk={handlerConfirm}
                onCancel={hideConfirmTieModal}
                okText="Ok"
                cancelText="Cancel"
            >
                {haveTieFromSelectedItem ? (
                    <p>
                        This item has a tie-point. The tie-point will be removed. Are you sure to add this point to
                        tie-point?
                    </p>
                ) : (
                    <p>Are you sure to add this point to tie-point?</p>
                )}
            </Modal>
            {showImportModal && fields && (
                <ControlPointsImportModal
                    projects={projectList}
                    fields={fields}
                    onCancel={() => {
                        setFileContent("");
                        setShowImportModal(false);
                    }}
                    callBack={(values: ControlPointsImportData, projects: ProjectRow[]) => {
                        importData(values, projects);
                        setProjectList(projects);
                        const projectItem = projects.find((p) => p.id === values.projectId);
                        if (projectItem) {
                            setSelectedProject({ ...projectItem, epsg: values.epsg });
                        }
                    }}
                />
            )}
        </ValidationTableContainer>
    ) : (
        <></>
    );
};
